import { useInterviewStore } from '@/stores/useInterviewStore'
import { FileText } from 'lucide-react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Typography from '@tiptap/extension-typography'
import { Markdown } from 'tiptap-markdown'
import { useEffect, useMemo, useRef, useCallback } from 'react'
import { Addition, Deletion, Note, parseAnnotations, serializeAnnotations } from '@/components/editor/AnnotationExtension'

interface FoolscapDocumentContentProps {
  editable?: boolean
}

export function FoolscapDocumentContent({ editable: editableProp = true }: FoolscapDocumentContentProps) {
  const {
    foolscapContent,
    foolscapDisplayContent,
    foolscapDocumentStreaming,
    foolscapDocumentPending,
    foolscapStreamingLineCount,
    workflowStatus,
    updateFoolscapContent,
  } = useInterviewStore()

  // Lock editing during document streaming (per design decision)
  const editable = editableProp && !foolscapDocumentStreaming && !foolscapDocumentPending

  // Document display: Use transient displayContent if available, fall back to persisted content
  // - foolscapDisplayContent: Set by streaming actions (not persisted, avoids race conditions)
  // - foolscapContent: Persisted to localStorage, set after streaming is fully complete
  // - Annotations (==additions==, ~~deletions~~) are rendered as highlights/strikethroughs
  const displayContent = foolscapDisplayContent ?? foolscapContent

  const editorContainerRef = useRef<HTMLDivElement>(null)
  const prevContentRef = useRef<string>('')
  const isExternalUpdateRef = useRef<boolean>(false)

  // Strip card UUIDs from display (keep in raw content for tracking)
  const stripCardIds = (content: string): string => {
    return content.replace(/\s*\[[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\]/gi, '')
  }

  // Parse annotation markers to HTML before passing to TipTap
  const parsedContent = useMemo(() => {
    console.log('[FoolscapDoc] displayContent length:', displayContent?.length)
    console.log('[FoolscapDoc] has annotations:', displayContent?.includes('==') || displayContent?.includes('~~'))
    if (!displayContent) return ''
    const strippedContent = stripCardIds(displayContent)
    const result = parseAnnotations(strippedContent)
    console.log('[FoolscapDoc] parsedContent preview:', result.substring(0, 300))
    return result
  }, [displayContent])

  // Handle user edits - sync back to store
  const handleEditorUpdate = useCallback(({ editor }: { editor: ReturnType<typeof useEditor> }) => {
    // Don't process edits during external updates or when not editable
    if (isExternalUpdateRef.current || !editable) {
      return
    }

    // Use tiptap-markdown to get proper markdown output (not HTML)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = editor?.storage as any
    if (storage?.markdown?.getMarkdown) {
      const markdown = storage.markdown.getMarkdown()
      // Convert HTML annotation marks back to markdown format
      const serialized = serializeAnnotations(markdown)

      if (serialized !== foolscapContent) {
        updateFoolscapContent(serialized)
      }
    }
  }, [foolscapContent, updateFoolscapContent, editable])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
      }),
      Markdown.configure({
        html: true,  // Allow HTML tags within markdown (for annotation marks)
        transformPastedText: true,
        transformCopiedText: true,
      }),
      Link.configure({
        openOnClick: false,
      }),
      Typography,
      Addition,
      Deletion,
      Note,
    ],
    content: parsedContent || '',
    editable,
    editorProps: {
      attributes: {
        class: 'prose-zinsser outline-none',
      },
    },
    immediatelyRender: false,
    onUpdate: handleEditorUpdate,
  }, []) // Empty deps - only create once

  // Update editable state when prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable)
    }
  }, [editor, editable])

  // Update editor content and trigger animations when content changes
  useEffect(() => {
    if (!editor) return

    const contentChanged = prevContentRef.current !== parsedContent
    prevContentRef.current = parsedContent

    isExternalUpdateRef.current = true
    // Use emitUpdate: false to prevent triggering onUpdate during external content set
    // The content contains markdown with HTML marks for annotations - tiptap-markdown handles both
    editor.commands.setContent(parsedContent || '', { emitUpdate: false })
    queueMicrotask(() => {
      isExternalUpdateRef.current = false
    })

    // Trigger animations on new annotations after content update
    if (contentChanged && parsedContent && editorContainerRef.current) {
      requestAnimationFrame(() => {
        const container = editorContainerRef.current
        if (!container) return

        const additions = container.querySelectorAll('.annotation-addition')
        const deletions = container.querySelectorAll('.annotation-deletion')

        additions.forEach(el => {
          el.classList.remove('annotation-animate')
          void (el as HTMLElement).offsetWidth
          el.classList.add('annotation-animate')
        })
        deletions.forEach(el => {
          el.classList.remove('annotation-animate')
          void (el as HTMLElement).offsetWidth
          el.classList.add('annotation-animate')
        })
      })
    }
  }, [editor, parsedContent])

  // Highlight last block during streaming for progress indication
  useEffect(() => {
    const container = editorContainerRef.current
    if (!container) return

    // Get all block-level elements (paragraphs, headings, list items)
    const blocks = container.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li')

    // If not streaming, remove all highlights
    if (!foolscapDocumentStreaming) {
      blocks.forEach(el => el.classList.remove('streaming-line-highlight'))
      return
    }

    // Remove highlight from all blocks first
    blocks.forEach(el => el.classList.remove('streaming-line-highlight'))

    // Highlight the LAST block element (where new content is being added)
    const lastBlock = blocks[blocks.length - 1]
    if (lastBlock) {
      lastBlock.classList.add('streaming-line-highlight')
      // Auto-scroll to keep the last block visible
      lastBlock.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }

    // Cleanup: remove highlights when effect re-runs or unmounts
    return () => {
      blocks.forEach(el => el.classList.remove('streaming-line-highlight'))
    }
  }, [foolscapDocumentStreaming, foolscapStreamingLineCount])

  return (
    <div className="h-full flex flex-col">
      {/* Editing indicator - shows at top when streaming */}
      {foolscapDocumentStreaming && (
        <div className="flex-shrink-0 px-4 py-2 border-b border-red-500/30 bg-red-500/10">
          <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
            <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="font-medium">Editing document...</span>
          </div>
        </div>
      )}

      {/* Document content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 relative">
        {displayContent ? (
          <div className="relative" ref={editorContainerRef}>
            <EditorContent editor={editor} />
          </div>
        ) : (foolscapDocumentPending || foolscapDocumentStreaming) ? (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <div className="inline-block w-2 h-2 bg-primary rounded-full animate-pulse mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Building document...
              </p>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <FileText className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Your Foolscap outline will appear here
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Type /foolscap to transform your brainstorm
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Copy button - show when not actively streaming */}
      {foolscapContent && workflowStatus !== 'streaming' && (
        <div className="flex-shrink-0 px-4 py-3 border-t border-border">
          <button
            onClick={() => {
              navigator.clipboard.writeText(foolscapContent)
            }}
            className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors text-center py-1"
          >
            Click to copy Foolscap
          </button>
        </div>
      )}
    </div>
  )
}
